// Key industrial-project lease annual rent calculation.
//
// Per rules/RULES_INDUSTRIAL.md §5 and logic/CALCULATION_INDUSTRIAL.md §5:
//
//   年租金底价 = 20年期地价 × 3%
//
// The implementation reuses the new-supply engine by running the
// standard calculation with `landTermYears` overridden to 20 for
// every item. Industry orientation / industry project-type factors
// are baked into `calculateItem` (see factor-helpers.ts), so the
// 0.5 / 0.6 / 0.7 discounts apply automatically. Per RULES_INDUSTRIAL.md
// §2 rule 1 / §3 rule 1, supporting commercial (配套商业) is excluded
// from those industry discounts — the existing helpers already
// return 1 for supporting commercial, so no special handling is
// needed here. The note field in the result makes this explicit.

import { MessageSink } from "./message-sink.js";

import {
  KEY_INDUSTRIAL_LEASE_ANNUAL_RATIO,
  KEY_INDUSTRIAL_LEASE_TERM_YEARS,
  type KeyIndustrialLeaseAnnualRentInput,
  type KeyIndustrialLeaseAnnualRentResult,
} from "../types/lease-rent.js";
import type { BuildingItem } from "../types/building-item.js";
import type { ValidationMessage } from "../types/validation.js";

import { calculateNewSupplyLandPrice } from "./new-supply.js";

/**
 * Compute the key industrial-project lease annual rent.
 *
 * Steps:
 *   1. Clone every input item with `landTermYears = 20` so the
 *      year-term factor resolves to K = [1 − 1/(1+r)^20] / [1 − 1/(1+r)^n]
 *      for each applicable price type.
 *   2. Run the standard `calculateNewSupplyLandPrice` on the cloned
 *      input. Industry factors (orientation 0.5, project-type 0.6 /
 *      0.7) apply automatically; supporting commercial stays at 1
 *      per the rules.
 *   3. Return `twentyYearLandPrice = totalLandPrice` and
 *      `annualRent = totalLandPrice × 3%`.
 */
export function calculateKeyIndustrialLeaseAnnualRent(
  input: KeyIndustrialLeaseAnnualRentInput,
): KeyIndustrialLeaseAnnualRentResult {
  const leaseItems: BuildingItem[] = input.items.map((item) => ({
    ...item,
    landTermYears: KEY_INDUSTRIAL_LEASE_TERM_YEARS,
  }));

  // Use a fresh sink so we can attach a reminder note about the
  // 3% rule without polluting the engine's own messages.
  const leaseSink = new MessageSink();
  leaseSink.info(
    "calculationType",
    `按 RULES_INDUSTRIAL.md §5：年租金底价 = 20 年期地价 × 3%（${(KEY_INDUSTRIAL_LEASE_ANNUAL_RATIO * 100).toFixed(0)}%）。`,
  );

  const baseResult = calculateNewSupplyLandPrice({
    parcel: input.parcel,
    prices: input.prices,
    items: leaseItems,
  });

  // Combine engine messages with our top-level note.
  const combinedMessages: ValidationMessage[] = [
    ...leaseSink.toArray(),
    ...baseResult.validationMessages,
  ];

  const twentyYearLandPrice = baseResult.totalLandPrice;
  const annualRent = twentyYearLandPrice * KEY_INDUSTRIAL_LEASE_ANNUAL_RATIO;

  // Detect any supporting commercial items and surface an info-level
  // reminder that those items were calculated at full price (no
  // industry discount) per RULES_INDUSTRIAL.md §2 / §3 rule 1.
  const supportingCommercial = leaseItems.filter(
    (i) => i.isSupportingFacility === true && i.supportingFacilityType === "配套商业",
  );
  let note: string | undefined;
  if (supportingCommercial.length > 0) {
    note =
      `配套商业（${supportingCommercial.map((i) => i.id).join("、")}）按 RULES_INDUSTRIAL.md §2/§3 规则 1 不享受产业修正折扣。`;
  }

  return {
    baseResult: {
      ...baseResult,
      validationMessages: combinedMessages,
    },
    twentyYearLandPrice,
    annualRent,
    validationMessages: combinedMessages,
    note,
  };
}